import React from 'react';
import { css, styled } from '@this/constants/themes';
import type SelectStore from '@this/src/domain/select_store';
import type SegmentClass from '@this/src/domain/transit/segment';
import type Transit from '@this/src/domain/transit/transit';
import IndividualRouteInfo from './IndividualRouteInfo';
import { BoxButtonText, Yen } from '../../../shared/box_parts';
import SelectTransportBoxPriceRangeLabel from '../../price_range_label';

interface IndividualDetailProps {
  transit: Transit;
  selected: boolean;
  domesticAirPriceIndex: number;
  handleChange: () => void;
  store: SelectStore;
  isRequestForm?: boolean;
  segment: SegmentClass;
  segmentIdx: number;
}

const IndividualDetail = ({
  transit,
  selected,
  domesticAirPriceIndex,
  handleChange,
  store,
  isRequestForm,
  segment,
  segmentIdx
}: IndividualDetailProps) => {
  const resolvePrice = (): number => {
    return transit.pricesOriginal?.[domesticAirPriceIndex]?.price || transit.price;
  };

  const renderIndividualRightPanel = (isTrainOrFlight: boolean, legSummaryPrice?: number) => {
    if (isTrainOrFlight) {
      const price = resolvePrice();
      const priceRange = store.result.type === 'airPackage' ? transit.price_range : undefined;
      const includeTax = transit.includeTax;

      return (
        <StyledIndividualBoxWrap>
          <StyledIndividualBoxPriceArea>
            <div>
              <StyledIndividualBoxPrice>
                {price !== undefined && (
                  <>
                    <span data-wovn-ignore>{utils.digits(price)}</span>
                    <Yen>円</Yen>
                  </>
                )}
                {priceRange && <SelectTransportBoxPriceRangeLabel priceRange={priceRange} />}
              </StyledIndividualBoxPrice>
              <StyledIndividualBoxSmall>
                /1名
                {includeTax ? '(税込)' : '(税別)'}
              </StyledIndividualBoxSmall>
            </div>
          </StyledIndividualBoxPriceArea>
          {isRequestForm === false && (
            <StyledIndividualBoxButtonArea>
              <StyledIndividualBoxButton selected={selected} onClick={handleChange}>
                <BoxButtonText className="select-transport-box__right-gray__button-text">
                  {selected ? (
                    <span>
                      変更を
                      <br />
                      キャンセル
                    </span>
                  ) : (
                    '変更 >'
                  )}
                </BoxButtonText>
              </StyledIndividualBoxButton>
            </StyledIndividualBoxButtonArea>
          )}
        </StyledIndividualBoxWrap>
      );
    }
    return (
      <StyledIndividualRightButtonPriceOnly>
        <StyledIndividualRightBody className="select-transport-box__right__body">
          <div>
            <StyledIndividualRightPrice>
              {legSummaryPrice !== undefined && (
                <>
                  <span data-wovn-ignore>{utils.digits(legSummaryPrice)}</span>
                  <Yen>円</Yen>
                </>
              )}
            </StyledIndividualRightPrice>
          </div>
        </StyledIndividualRightBody>
      </StyledIndividualRightButtonPriceOnly>
    );
  };

  return (
    <StyledLegSummariesContainer>
      {segment.leg_summaries.map((legSummary, j) => {
        const isEmptyPrice = legSummary.price === 0;
        const isWalking = legSummary.name === '徒歩';
        const hasNoName = legSummary.name === '';
        const isTrainOrFlight = legSummary.type === 'jr' || legSummary.type === 'air';

        // MEMO: リファクタ時に判定しきれなかったけど　これ不要感ある。stateの変更はないはず
        const isError = isEmptyPrice && !isWalking && !hasNoName;
        if (isError) {
          return <StyledError key={`error_${segmentIdx}_${j}`}>経路の検索に失敗しました</StyledError>;
        }

        return (
          <StyledLegSummaryItem
            key={`express_short_distance_${segmentIdx}_${j}`}
            className={isTrainOrFlight ? 'select-transport-box__button_exist_leg_shortdistance__separate' : ''}
          >
            <IndividualRouteInfo legSummary={legSummary} segment={segment} isTrainOrFlight={isTrainOrFlight} />

            <div className="select-transport-box__right-wrapper-shortdistance">
              {renderIndividualRightPanel(isTrainOrFlight, legSummary.price ?? 0)}
            </div>
          </StyledLegSummaryItem>
        );
      })}
    </StyledLegSummariesContainer>
  );
};

const StyledError = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: ${props => props.theme.redColor};
`;

const StyledLegSummaryItem = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
`;

const StyledLegSummariesContainer = styled.div`
  flex-shrink: 10;
  padding: 0;
  width: 100%;
`;

const StyledIndividualBoxWrap = styled.div`
  display: flex;
  flex-grow: 0;
  margin-left: auto;
  min-width: 170px;
  color: ${props => (props.theme.themeClass === 'tabikobo' ? props.theme.textColor : props.theme.linkColor)};
`;

const StyledIndividualBoxPriceArea = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column;
  margin: auto 0 auto 10px;
`;

const StyledIndividualBoxPrice = styled.span`
  color: ${props => (props.theme.themeClass === 'tabikobo' ? props.theme.textColor : props.theme.moneyColor)};
  font-size: 14px;
  font-weight: bold;
  display: block;
`;

const StyledIndividualBoxSmall = styled.div`
  font-size: 10px;
  display: inline-block;
`;

const StyledIndividualBoxButtonArea = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column;
  margin: auto 13px auto auto;
`;

const StyledIndividualBoxButton = styled.div<{ selected: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;

  width: 53px;
  height: 30px;
  text-align: center;
  color: ${props => props.theme.linkColor};
  background: white;
  border: 2px solid ${props => props.theme.linkColor};
  border-radius: 3px;
  padding: auto 2px;
  font-weight: bold;
  line-height: 11px;
  font-size: 11px;
  cursor: pointer;

  ${props =>
    props.selected &&
    `
    background: ${props.theme.linkColor};
    color: white;
    font-size: 9px;
    letter-spacing: -1px;
  `};

  ${({ disabled }) =>
    disabled
      ? css`
          opacity: 0.6;
          pointer-events: none;
        `
      : null}
`;

const StyledIndividualRightButtonPriceOnly = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px;
  margin: 2px;
  color: ${props => props.theme.linkColor};
  font-weight: bold;
  cursor: pointer;
  min-width: 87px;
  height: 70px;
  &.flight {
    min-width: 116px;
  }
`;

const StyledIndividualRightBody = styled.div`
  text-align: center;
  margin-left: auto;
  margin-right: auto;
`;

const StyledIndividualRightPrice = styled.span`
  font-size: 14px;
`;

export default IndividualDetail;
